import { SearchOff, Error } from '@mui/icons-material'
import { Paper, Typography, PaperProps, CircularProgress, Box } from '@mui/material'

export interface EmptyStateProps extends PaperProps {
  title?: string
  description?: string
  emptyStateVariant: 'loading' | 'error' | 'empty' | 'no-icon'
}

const EmptyState = ({
  title,
  description,
  emptyStateVariant,
  sx,
  ...paperProps
}: EmptyStateProps) => {
  let safeTitle = title
  let safeDescription = description

  if (!title || !description) {
    switch (emptyStateVariant) {
      case 'loading':
        safeTitle = title ?? 'Loading data'
        safeDescription = description ?? 'Please wait while we load the data'
        break
      case 'error':
        safeTitle = title ?? 'Error getting data'
        safeDescription = description ?? 'An error occurred while getting the data'
        break
      case 'empty':
      default:
        safeTitle = title ?? 'No data'
        safeDescription = description ?? 'No data found'
        break
    }
  }

  const renderIcon = () => {
    switch (emptyStateVariant) {
      case 'loading':
        return <CircularProgress size={'5rem'} color="primary" />
      case 'error':
        return <Error sx={{ fontSize: '5rem' }} color="error" />
      case 'empty':
        return <SearchOff sx={{ fontSize: '5rem' }} color="primary" />
      default:
        return null
    }
  }

  return (
    <Paper
      elevation={0}
      sx={{
        p: 4,
        maxWidth: '600px',
        width: '100%',
        mx: 'auto',
        ...sx,
      }}
      {...paperProps}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        {renderIcon()}

        <Box sx={{ marginTop: emptyStateVariant === 'no-icon' ? '0' : '16px' }}>
          <Typography variant="h5" textAlign="center" gutterBottom>
            {safeTitle}
          </Typography>
          <Typography variant="body1" textAlign="center" color="text.secondary">
            {safeDescription}
          </Typography>
        </Box>
      </Box>
    </Paper>
  )
}

export default EmptyState
